import React, { useEffect, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  BillingMethod,
  Feature,
  Offer,
  QuoteInformation,
  StudyType,
  useCapabilities,
  useBillingInformationsByOrga,
  useCurrentOrganizationId,
  useMe,
  useOffers,
  usePriceTiers,
} from '@app/api';
import {
  CountryKeySelect,
  OfferList,
  OfferOptionBlock,
  SubmitButton,
} from 'components';
import { SetupSection } from '../components/SetupSection';
import { SupportSection } from './Sections/SupportSection';
import {
  requirePriceTier,
  useInvoiceFeeOffer,
  useOfferInfos,
} from './filterOffers';
import { StudyTypeSection } from './Sections/StudyTypeSection';
import { StudyFormatModeratedSection } from './Sections/StudyFormatModeratedSection';
import { OwnRecruitmentSection } from './Sections/OwnRecruitmentSection';
import { StudyFormatUnmoderatedSection } from './Sections/StudyFormatUnmoderatedSection';
import { TalkToSalesSection } from './Sections/TalkToSalesSection';
import { SourceSection } from './Sections/SourceSection';
import { StudySubjectSection } from './Sections/StudySubjectSection';
import { GroupMeetingSection } from './Sections/GroupMeetingSection';
import { DurationSection } from './Sections/DurationSection';
import { BillingMethodSection } from './Sections/BillingMethodSection';
import { TotalSection } from './Sections/TotalSection';
import { NumberOfParticipantsSection } from './Sections/NumberOfParticipantsSection';

const useStyles = createUseStyles((theme: Theme) => ({
  page: {},
  bottomSpace: {
    height: theme.marginBase * 40,
  },
  singleButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactBannerContainer: {
    marginTop: theme.marginBase * 3,
  },
}));

export interface GeneralSetupProps {
  askForSource?: boolean;
  head?: React.ReactNode;
}

export const GeneralSetup = ({ head, askForSource }: GeneralSetupProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { data: me } = useMe(true);
  const logged = !!me;
  const capabilities = useCapabilities();
  const currentOrganizationId = useCurrentOrganizationId();
  const { data: billingInformations } = useBillingInformationsByOrga(
    currentOrganizationId,
  );
  const { data: offers } = useOffers();
  const invoiceFeeOffer = useInvoiceFeeOffer();

  const {
    availableOffers,
    targetingOffers,
    ownRecruitmentOffer,
    optionOffers,
    supportOffers,
  } = useOfferInfos();
  usePriceTiers();

  const { values, setFieldValue } =
    useFormikContext<Partial<QuoteInformation>>();

  const shouldAddInvoiceFees =
    invoiceFeeOffer &&
    !capabilities?.[Feature.SEND_INVOICE] &&
    values.billingMethod === BillingMethod.SEND_INVOICE;

  const showPriceTier = useMemo(
    () => requirePriceTier(values, offers),
    [values, offers],
  );

  const selectedOffers = useMemo(() => {
    return [values.targetingOfferId, values.supportOfferId]
      .map((id) => {
        return availableOffers.find((offer) => offer.id === id);
      })
      .concat(
        values.optionsOfferIds?.map((id) => {
          return optionOffers.find((offer) => offer.id === id);
        }),
      )
      .concat(shouldAddInvoiceFees ? [invoiceFeeOffer] : [])
      .filter(Boolean) as Offer[];
  }, [
    values.targetingOfferId,
    values.supportOfferId,
    values.optionsOfferIds,
    values,
    availableOffers,
    optionOffers,
    invoiceFeeOffer,
    shouldAddInvoiceFees,
  ]);

  useEffect(() => {
    if (
      billingInformations &&
      billingInformations.billingAddress &&
      billingInformations.billingAddress.country
    ) {
      setFieldValue('organizationId', billingInformations.organizationId);
    }

    if (billingInformations && billingInformations.defaultStudyBillingMethod) {
      setFieldValue(
        'billingMethod',
        billingInformations.defaultStudyBillingMethod,
      );
    }
  }, [billingInformations, setFieldValue]);

  const offerRequireCallToSales = selectedOffers.some(
    (o) => !!o.requireCallToSales,
  );

  useEffect(() => {
    const offerIds = availableOffers.map(({ id }) => id);

    if (
      values.targetingOfferId &&
      !offerIds.includes(values.targetingOfferId) &&
      values.targetingOfferId !== ownRecruitmentOffer?.id
    ) {
      setFieldValue('targetingOfferId', undefined);
    }
    if (values.supportOfferId && !offerIds.includes(values.supportOfferId)) {
      setFieldValue('supportOfferId', undefined);
    }
    if (values.priceThierId && !showPriceTier) {
      setFieldValue('priceThierId', undefined);
    }
  }, [values, availableOffers, showPriceTier, ownRecruitmentOffer?.id]);

  const willSelectOffer =
    !!values.studySubject &&
    !!values.studyType &&
    !!values.format &&
    values.source === 'TANDEMZ';

  return (
    <div className={classes.page}>
      {head}

      {(!!askForSource || !values.source) && (
        <SetupSection
          title={t('studySetup.generalSetup.source.title')}
          visible
          trackId="source"
        >
          <SourceSection />
        </SetupSection>
      )}
      <SetupSection
        title={t('studySetup.generalSetup.studySubject.title')}
        visible={values.source === 'TANDEMZ'}
        trackId="studySubject"
      >
        <StudySubjectSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.studyType.title')}
        visible={!!values.studySubject || values.source === 'CLIENT'}
        trackId="studyType"
      >
        <StudyTypeSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.groupMeeting.title')}
        visible={values.studyType === StudyType.MODERATED}
        trackId="studyGroupMeeting"
      >
        <GroupMeetingSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.format.title')}
        visible={
          values.studyType === StudyType.MODERATED &&
          values.groupMeeting !== undefined
        }
        trackId="studyFormat"
      >
        <StudyFormatModeratedSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.format.titleUnmoderated')}
        visible={values.studyType === StudyType.UNMODERATED}
        trackId="studyFormat"
      >
        <StudyFormatUnmoderatedSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.duration.title')}
        visible={
          willSelectOffer && (targetingOffers.length > 0 || showPriceTier)
        }
        trackId="duration"
      >
        <DurationSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.priceTier.title')}
        visible={
          (willSelectOffer && !!values.duration) ||
          (!!values.studyType && !!values.format && values.source === 'CLIENT')
        }
        trackId="priceTiers"
      >
        <NumberOfParticipantsSection showQuantityPriceTier={showPriceTier} />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.countryKeys.title')}
        visible={
          willSelectOffer &&
          !!targetingOffers.length &&
          !!values.duration &&
          !!values.numberOfParticipantRequired
        }
        trackId="countryKeys"
      >
        <CountryKeySelect isMulti name="countryKeys" />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.offers.title')}
        visible={
          willSelectOffer &&
          (!showPriceTier || !!values.priceThierId) &&
          !!targetingOffers.length &&
          !!values.duration &&
          !!values.countryKeys?.length
        }
        trackId="targetingOffers"
      >
        <OfferList
          offers={targetingOffers}
          name="targetingOfferId"
          duration={values.duration}
          countryKeys={values.countryKeys}
        />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.support.title')}
        visible={
          willSelectOffer &&
          !!values.targetingOfferId &&
          !!supportOffers.length &&
          (!offerRequireCallToSales || !!values.supportOfferId)
        }
        trackId="supportOffers"
      >
        <SupportSection
          offers={supportOffers}
          selectedOffers={selectedOffers}
        />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.option.title')}
        visible={
          willSelectOffer &&
          !!values.targetingOfferId &&
          (!!values.supportOfferId || !supportOffers.length) &&
          (!offerRequireCallToSales || !!values.optionsOfferIds?.length) &&
          optionOffers.length > 0
        }
        trackId="options"
      >
        <OfferOptionBlock offers={optionOffers} />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.billingMethod.title')}
        visible={
          willSelectOffer &&
          !!values.targetingOfferId &&
          (!!values.supportOfferId || !supportOffers.length) &&
          !offerRequireCallToSales &&
          (!!values.optionsOfferIds || !optionOffers.length) &&
          !logged
        }
        trackId="clientBillingMethod"
      >
        <BillingMethodSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.cost.title')}
        visible={
          willSelectOffer &&
          !!values.targetingOfferId &&
          (!!values.supportOfferId || !supportOffers.length) &&
          !offerRequireCallToSales &&
          (!!values.optionsOfferIds || !optionOffers.length) &&
          (!!logged || (!!values.billingMethod && !!values.billingCountry))
        }
        trackId="total"
        finalSection
      >
        <TotalSection offers={selectedOffers} />
      </SetupSection>
      <SetupSection
        visible={
          offerRequireCallToSales ||
          (willSelectOffer &&
            !targetingOffers.length &&
            (!showPriceTier || !!values.priceThierId)) ||
          values.studyType === 'DIARY'
        }
        title={
          values.studyType === 'DIARY'
            ? t('studySetup.generalSetup.diary.title')
            : t('studySetup.generalSetup.talkToSales.title')
        }
        trackId="talkToSales"
      >
        <TalkToSalesSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.ownIncentive.title')}
        visible={
          values.source === 'CLIENT' && !!values.numberOfParticipantRequired
        }
        trackId="ownIncentive"
      >
        <OwnRecruitmentSection offer={ownRecruitmentOffer} />
      </SetupSection>

      {values.source === 'CLIENT' && (
        <div className={classes.singleButtonContainer}>
          <SubmitButton
            disabled={
              !values.studyType ||
              (!values.format && values.studyType === 'MODERATED') ||
              !values.ownRecruitmentOption
            }
            text={t('studySetup.generalSetup.continue')}
            withNextArrow
            color="accent"
          ></SubmitButton>
        </div>
      )}
      {!values.targetingOfferId && values.source !== 'CLIENT' && (
        <div className={classes.bottomSpace} />
      )}
    </div>
  );
};
